import { Divider } from "@material-ui/core";
import { Container, Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  CustomTable,
  FormLabel,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
  TextInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";

const PublisherUploadDirectory = () => {
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const pageTitle = "Publisher Upload Directory";

  const [loadingPage, setLoadingPage] = useState(false);
  const [optionDsp, setOptionDsp] = useState([]);
  const [payload, setPayload] = useState({
    ccid_target_path: "",
    directories: [],
  });
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    sftp_config_id: 0,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeDSP = e => {
    const { value } = e?.target;
    setPayload({ ccid_target_path: "", directories: [] });
    setDataTable([]);
    handleChangeQueryParams(value, "sftp_config_id");
  };
  const handleChangeSearch = event => {
    const { value } = event?.target;
    handleChangeQueryParams(value, "search");
  };
  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangeDirectory = (value, item) => {
    const publisherId = item?.publisher_id;
    if (!publisherId) {
      return;
    }

    setPayload(currentState => {
      const currentDirectories = currentState?.directories || [];

      const existingIndex = currentDirectories.findIndex(
        directory => directory?.publisher_id === publisherId
      );
      if (existingIndex === -1) {
        return {
          ...currentState,
          directories: [
            ...currentDirectories,
            { publisher_id: publisherId, directory_name: value },
          ],
        };
      }
      return {
        ...currentState,
        directories: currentDirectories.map((directory, index) =>
          index === existingIndex
            ? { ...directory, directory_name: value }
            : directory
        ),
      };
    });
    setDataTable(currentData =>
      currentData.map(row =>
        row?.publisher_id === publisherId
          ? { ...row, directory_name: value }
          : row
      )
    );
  };
  const handleSave = () => {
    ModalWarning("Are you sure want to save changes?").then(
      res => res.isConfirmed && postSubmit()
    );
  };
  const mergeApiDataWithChanges = apiData => {
    const changes = payload?.directories || [];
    return (apiData || []).map(item => {
      const changedItem = changes.find(
        directory => directory?.publisher_id === item?.publisher_id
      );
      if (!changedItem) {
        return item;
      }
      return { ...item, directory_name: changedItem?.directory_name };
    });
  };

  const getOptionDsp = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/dsp-sftp/list-dsp-upload`, {
        headers,
      });
      setOptionDsp(res?.data?.data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDetails = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/dsp-sftp/directories`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      const apiTableData = data?.directories || [];
      setPayload(currentState => {
        const apiTargetPath = data?.ccid_target_path || "";
        return {
          ...currentState,
          ccid_target_path: currentState?.ccid_target_path || apiTargetPath,
          directories: currentState?.directories || [],
        };
      });
      setDataTable(mergeApiDataWithChanges(apiTableData));
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDetails = useCallback(
    debounce(() => {
      getDetails();
    }, 500),
    [queryParams]
  );
  const postSubmit = async () => {
    const modifiedPayload = {
      ...payload,
      sftp_config_id: queryParams?.sftp_config_id,
    };
    try {
      await axios.post(`${hardBaseUrl}/dsp-sftp/directories`, modifiedPayload, {
        headers,
      });
      ModalSuccess("Successfully save changes").then(() => getDetails());
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  useEffect(() => {
    getOptionDsp();
  }, []);
  useEffect(() => {
    if (queryParams?.sftp_config_id) {
      const isSearching = Boolean(queryParams?.search);
      if (isSearching) {
        debounceDetails();
        return () => {
          debounceDetails.cancel();
        };
      } else {
        getDetails();
      }
    }
  }, [queryParams, debounceDetails]);
  useEffect(() => {
    if (!dataTable?.length) {
      return;
    }
    const changes = payload?.directories || [];
    if (!changes.length) {
      return;
    }
    setDataTable(currentData =>
      currentData.map(item => {
        const changedItem = changes.find(
          directory => directory?.publisher_id === item?.publisher_id
        );
        return changedItem
          ? { ...item, directory_name: changedItem?.directory_name }
          : item;
      })
    );
  }, [payload?.directories]);

  return (
    <Page className={classes.root} title={pageTitle}>
      <Container maxWidth={false}>
        <HeaderTitle
          title="Configuration"
          breadcrumbData={breadcrumbData({ pageTitle })}
        />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          my={2}
        >
          <Grid item>
            <Grid container columnSpacing={2}>
              <Grid item>
                <FormLabel label="DSP" />
                <SelectInput
                  options={optionDsp}
                  optionKey="config_id"
                  optionLabel="dsp_config_name"
                  value={queryParams?.sftp_config_id || ""}
                  onChange={handleChangeDSP}
                  placeholder="Select DSP"
                  width={200}
                />
              </Grid>
              {!!queryParams?.sftp_config_id && (
                <Grid item>
                  <FormLabel label="Search" />
                  <SearchTextInput
                    placeholder="Search"
                    value={queryParams?.search}
                    onChange={handleChangeSearch}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          {!!queryParams?.sftp_config_id && (
            <Grid item>
              <PrimaryButton label="Save" onClick={handleSave} />
            </Grid>
          )}
        </Grid>
        {!!queryParams?.sftp_config_id && (
          <Grid container direction="column" rowGap={1} my={2}>
            <Grid item>
              <FormLabel label="Target Path" />
              <TextInput
                value={payload?.ccid_target_path}
                onChange={e =>
                  handleChangePayload(e?.target?.value, "ccid_target_path")
                }
                width="400px"
              />
            </Grid>
            {helperText?.map(({ variable, description }) => (
              <Grid item key={variable}>
                <Typography fontSize={12}>
                  <Typography component="span" fontSize={12} color="#7737BC">
                    {variable}
                  </Typography>
                  {` = ${description}`}
                </Typography>
              </Grid>
            ))}
          </Grid>
        )}
        {queryParams?.sftp_config_id ? (
          <CustomTable
            isLoading={loadingPage}
            columnTable={columnTable({ handleChangeDirectory })}
            data={dataTable || []}
            page={queryParams?.page}
            pageSize={queryParams?.size}
            pageCount={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangePageSize={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
          />
        ) : (
          <Typography
            fontSize={18}
            fontWeight={400}
            color="#687083"
            align="center"
          >
            Select DSP first to process the data
          </Typography>
        )}
      </Container>
    </Page>
  );
};

const breadcrumbData = ({ pageTitle }) => [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Configuration",
    link: "/admin/konfigurasi/publisher-directory",
  },
  {
    label: pageTitle,
    active: true,
  },
];
const helperText = [
  {
    variable: "{{PUBLISHER_DIR}}",
    description: "Nama folder publisher (kolom \"Folder Name\" di tabel bawah)",
  },
  { variable: "{{PERIOD}}", description: "Folder periode CCID" },
];
const columnTable = ({ handleChangeDirectory }) => [
  {
    name: "publisher_name",
    title: "Publisher Name",
  },
  {
    name: "all",
    title: "Folder Name",
    renderText: item => (
      <TextInput
        value={item?.directory_name}
        onChange={e => handleChangeDirectory(e?.target?.value, item)}
      />
    ),
  },
];
export default PublisherUploadDirectory;
